'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { useAuth } from '@clerk/nextjs'
import { BellAlertIcon, BoltIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useStations } from '@/contexts/stations-context'

const API_BASE = process.env.NEXT_PUBLIC_BACKEND_API || 'https://backend.hazcams.com'
const MAX_RESULTS_PER_PRODUCT = 6

export default function Search() {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [pins, setPins] = useState([])
  const [pinsLoading, setPinsLoading] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)
  const rafRef = useRef(null)
  const router = useRouter()
  const { stations, loading: stationsLoading } = useStations()
  const { getToken, has, isLoaded: authLoaded, orgId } = useAuth()
  const strikeZoneEnabled = authLoaded && Boolean(orgId && has?.({ feature: 'strike_zone' }))
  const hazAlertsEnabled = authLoaded && Boolean(orgId && has?.({ feature: 'hazalerts' }))
  const pinsEnabled = strikeZoneEnabled || hazAlertsEnabled

  useEffect(() => {
    if (!pinsEnabled) {
      setPins([])
      setPinsLoading(false)
      return undefined
    }

    const controller = new AbortController()
    const loadPins = async () => {
      setPinsLoading(true)
      try {
        const token = await getToken()
        const response = await fetch(`${API_BASE}/location-pins`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Unable to load product locations.')
        const data = await response.json()
        setPins(Array.isArray(data.pins)
          ? data.pins.filter((pin) =>
            (strikeZoneEnabled && pin.strikeZone) || (hazAlertsEnabled && pin.hazAlerts)
          )
          : [])
      } catch (error) {
        if (error.name !== 'AbortError') setPins([])
      } finally {
        if (!controller.signal.aborted) setPinsLoading(false)
      }
    }

    loadPins()
    return () => controller.abort()
  }, [getToken, hazAlertsEnabled, orgId, pinsEnabled, strikeZoneEnabled])

  const { results, totalResults } = useMemo(() => {
    const searchTerm = query.trim().toLowerCase()
    if (!searchTerm) return { results: [], totalResults: 0 }

    const stationResults = (stations || []).filter((station) => {
      const name = station.name || station.station_name || ''
      const id = station.internalId || station.id || station.station_id || ''
      return [name, id, station.location].some((value) => String(value || '').toLowerCase().includes(searchTerm))
    }).map((station) => {
      const id = station.internalId || station.id || station.station_id
      return {
        key: `station:${id}`,
        type: 'HazCam',
        title: station.name || station.station_name || 'Unknown Station',
        subtitle: [id ? `ID: ${id}` : '', station.location || ''].filter(Boolean).join(' · '),
        href: `/stations/${id}`,
        station,
      }
    })

    const matchingPins = pins.filter((pin) =>
      [pin.name, pin.description, pin.uuid].some((value) => String(value || '').toLowerCase().includes(searchTerm))
    )

    const hazAlertResults = hazAlertsEnabled ? matchingPins.filter((pin) => pin.hazAlerts).map((pin) => ({
      key: `hazalerts:${pin.uuid}`,
      type: 'HazAlerts',
      title: pin.name,
      subtitle: pin.description || `${Number(pin.lat).toFixed(4)}, ${Number(pin.lon).toFixed(4)}`,
      href: `/hazalerts/${pin.uuid}`,
      pin,
    })) : []

    const strikeZoneResults = strikeZoneEnabled ? matchingPins.filter((pin) => pin.strikeZone).map((pin) => ({
      key: `strike-zone:${pin.uuid}`,
      type: 'Strike Zone',
      title: pin.name,
      subtitle: pin.description || `${Number(pin.lat).toFixed(4)}, ${Number(pin.lon).toFixed(4)}`,
      href: `/strike-zone/${pin.uuid}`,
      pin,
    })) : []

    return {
      results: [
        ...stationResults.slice(0, MAX_RESULTS_PER_PRODUCT),
        ...hazAlertResults.slice(0, MAX_RESULTS_PER_PRODUCT),
        ...strikeZoneResults.slice(0, MAX_RESULTS_PER_PRODUCT),
      ],
      totalResults: stationResults.length + hazAlertResults.length + strikeZoneResults.length,
    }
  }, [hazAlertsEnabled, pins, query, stations, strikeZoneEnabled])

  const showDropdown = isOpen && query.trim().length > 0
  const loading = stationsLoading || pinsLoading

  const closeSearch = () => {
    setQuery('')
    setIsOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()
  }

  const selectResult = (result) => {
    closeSearch()
    router.push(result.href)
  }

  const handleKeyDown = (event) => {
    if (!showDropdown) return
    if (event.key === 'Escape') {
      event.preventDefault()
      setIsOpen(false)
      setHighlightedIndex(-1)
      inputRef.current?.blur()
      return
    }
    if (!results.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((current) => current < results.length - 1 ? current + 1 : 0)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((current) => current > 0 ? current - 1 : results.length - 1)
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault()
      selectResult(results[highlightedIndex])
    }
  }

  useEffect(() => {
    setMounted(true)
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const updateDropdownPosition = () => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
  }

  useEffect(() => {
    if (showDropdown) updateDropdownPosition()
  }, [query, showDropdown])

  useEffect(() => {
    if (!showDropdown) return undefined
    const handleReposition = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateDropdownPosition)
    }
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    const resizeObserver = typeof ResizeObserver !== 'undefined' && inputRef.current
      ? new ResizeObserver(handleReposition)
      : null
    resizeObserver?.observe(inputRef.current)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
      resizeObserver?.disconnect()
    }
  }, [showDropdown])

  return (
    <div className="relative flex-1 self-stretch rounded-md bg-input">
      <div className="grid h-full grid-cols-1">
        <input
          ref={inputRef}
          name="search"
          type="text"
          role="searchbox"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setHighlightedIndex(-1)
            setIsOpen(true)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder="Search..."
          aria-label="Search"
          aria-expanded={showDropdown}
          className="col-start-1 row-start-1 block h-full w-full rounded-md bg-transparent pl-8 text-base text-foreground outline-hidden placeholder:text-foreground-subtle sm:text-sm/6"
          autoComplete="off"
        />
        <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 ml-2 size-5 self-center text-foreground-subtle" />
      </div>

      {mounted && showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed max-h-80 overflow-y-auto rounded-md bg-[var(--color-panel)] shadow-lg ring-1 ring-[var(--color-panel-border)]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999,
          }}
        >
          {loading && <div className="px-4 py-3 text-sm text-foreground-muted">Searching products...</div>}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-foreground-muted">No results found for “{query}”</div>
          )}
          {!loading && results.map((result, index) => (
            <button
              key={result.key}
              type="button"
              onClick={() => selectResult(result)}
              onMouseEnter={() => setHighlightedIndex(index)}
              aria-selected={highlightedIndex === index}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-[var(--color-panel-hover)] ${highlightedIndex === index ? 'bg-[var(--color-panel-hover)]' : 'bg-transparent'}`}
            >
              {result.station ? (
                <div className="size-8 shrink-0 overflow-hidden rounded-full bg-background-alt ring-1 ring-zinc-300 dark:ring-zinc-700">
                  <img
                    src={`${process.env.NEXT_PUBLIC_DATA_API}/thumbnails/${result.station.internalId || result.station.id || result.station.station_id}/small.webp`}
                    alt=""
                    className="size-full object-cover"
                    onError={(event) => { event.currentTarget.style.display = 'none' }}
                  />
                </div>
              ) : result.type === 'HazAlerts' ? (
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 ring-1 ring-red-200 dark:bg-red-950 dark:text-red-300 dark:ring-red-900">
                  <BellAlertIcon className="size-4" />
                </div>
              ) : (
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600 ring-1 ring-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:ring-sky-900">
                  <BoltIcon className="size-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-foreground">{result.title}</span>
                  <span className="shrink-0 rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">{result.type}</span>
                </div>
                <div className="truncate text-xs text-foreground-muted">{result.subtitle}</div>
              </div>
            </button>
          ))}
          {!loading && totalResults > results.length && (
            <div className="border-t border-border px-4 py-2 text-xs text-foreground-muted">
              Showing {results.length} of {totalResults} results
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
